<script>
import { mapState, mapGetters } from 'vuex'
import TokenGenerator from 'uuid-token-generator'
import { zendeskActions, zendeskValidateActions } from '@state/helpers'
import attachment from '@/mixins/attachment-utils.js'
import SubHeader from '@components/sub-header'
import ImageHolder from '@components/image-holder'

const IOS_DEVICE_OS = 'iOS'
const DEVICE_ANDROID = 'Android'

export default {
  name: 'UploadImage',
  components: {
    SubHeader,
    ImageHolder,
  },
  mixins: [attachment()],
  props: {
    componentData: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      imageFile: null,
      tokgen: new TokenGenerator(),
    }
  },
  computed: {
    app () {
      return app || this.app // eslint-disable-line
    },
    ...mapState('widgets', {
      userOS: state => state.common.user.os,
    }),
    ...mapState('zendesk', {
      uploads: state => state.uploads,
      files: state => state.files,
    }),
    ...mapGetters('widgets', ['notSatisfiedUser', 'userType']),
    isUserRT () {
      return this.userType === 'storekeepers'
    },
  },
  watch: {
    imageFile () {
      const { imageFile } = this

      this.getBase64(imageFile).then(src => {
        const file = Object.assign(
          {},
          { imageFile },
          { src },
          { token: this.tokgen.generate() }
        )

        this.addAttachmentFile(file)
      })
    },
    files () {
      const { name } = this.$options
      if (this.files.length > 0) {
        this.commitValidatedField({
          type: 'function',
          key: 'attachmentValidation',
          name,
          value: this.files,
          message: this.$t('COMMON.REQUIRED_IMAGE.BASE', {
            n: 1,
          }),
        })
      } else {
        this.removeValidatedField({ key: 'attachmentValidation' })
      }
    },
  },
  created () {
    this.exposeUploaderMethods()
    this.validateIfRequired()
  },
  mounted () {
    if (this.userOS === IOS_DEVICE_OS) {
      this.disableFileSelector()
    }
  },
  methods: {
    ...zendeskActions,
    ...zendeskValidateActions,
    handleImageUpload () {
      this.imageFile = this.$refs.file.files[0]
    },
    retrieveImageFromDevice (imageBytes) {
      this.imageFile = this.createFileFromBase64Uri(imageBytes)
    },
    manageImageUpload () {
      const {
        enable_webkit: eneableWebkit
      } = this.$route.query
      if ((this.userOS === IOS_DEVICE_OS) && eneableWebkit) window.webkit.messageHandlers.requestCameraPermissions.postMessage("open camera")
      if (this.callAndriod()) {
        this.app.requestCameraPermissions()
      }
    },
    functionExist(possibleFunction) {
      return typeof(possibleFunction) === typeof(Function)
    },
    callAndriod() {
      return this.userOS === DEVICE_ANDROID && this.functionExist(this.app.requestCameraPermissions)
    },
    exposeUploaderMethods () {
      window.webUploader = {
        retrieveImageFromDevice: this['retrieveImageFromDevice'],
        disableFileSelector: this['disableFileSelector'],
      }
    },
    onRemoveImage (token) {
      this.removeZendeskAttachment(token)
    },
    disableFileSelector () {
      this.$refs.file.disabled = true
    },
    validateIfRequired () {
      const { name } = this.$options
      if (this.componentData.required) {
        this.commitRequiredFields({
          type: 'function',
          key: 'attachmentValidation',
          name,
          message: this.$t('COMMON.REQUIRED_IMAGE.BASE', {
            n: 1,
          }),
        })
      }
    },
  },
}
</script>

<template>
  <div
    v-if="notSatisfiedUser"
    :class="$style.container"
  >
    <SubHeader
      :class="$style.container__header"
      :text="componentData.title"
    />

    <div :class="$style.wrapper">
      <input
        id="file"
        ref="file"
        :class="$style.container__attachFile"
        accept="image/*"
        type="file"
        name="file"
        @change="handleImageUpload()"
      >
      <!-- <transition
        v-if="uploads.length < 2"
        appear
      > -->
      <label
        v-if="files.length < 8"
        :class="$style.container__label"
        for="file"
        @click="manageImageUpload()"
      >
        <div
          id="placeholder"
          :class="$style.container__labelPlaceholder"
        >
          <BaseIcon
            :class="[{ [$style.iconRT]: isUserRT },$style.icon]"
            name="plus"
          />
        </div>
      </label>
      <!-- </transition> -->

      <transition
        v-for="f in files"
        :key="f.imageFile.name"
        appear
      >
        <ImageHolder
          :img-src="f.src"
          @removeImage="onRemoveImage(f.token)"
        />
      </transition>

    </div>

  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  padding-top: 1rem;
  padding-bottom: 1rem;
  margin: $size-spacing-medium 0;
  background-color: $background-container;

  &__header {
    margin-bottom: 2rem;
  }

  &__label {
    display: flex;
    justify-content: flex-start;

    &Placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 6rem;
      height: 6rem;
      background-color: $white;
      border-radius: 0.5rem;
      box-shadow: $regular-box-shadow;
    }

    // stylelint-disable-next-line selector-class-pattern
    &:global(.v-enter-active) {
      transition: opacity 1s;
    }
    // stylelint-disable-next-line selector-class-pattern
    &:global(.v-enter) {
      opacity: 0;
    }
  }

  &__attachFile {
    position: absolute;
    z-index: -1;
    width: 0;
    height: 0;
    overflow: hidden;
    opacity: 0;
  }
}

.wrapper {
  display: grid;
  // grid-template-rows: repeat(2, 150px);
  grid-template-columns: 1fr 1fr;
  grid-gap: 2rem;
  padding: 0 1rem;
  margin: 0;

  @include responsive(phoneS) {
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 1rem;
  }
}

.icon {
  font-size: 1.7rem;
  color: $rappi-green;

  &RT {
    // color: $color-rt-cian-primar;
    color: $color-rt-cian-primary !important;
  }
}
</style>
